import { useAlCambiar, useDatos } from "../api";
import { Carga } from "../componentes/Carga";
import { BadgeCanal, BadgeMensaje } from "../componentes/Estados";
import { Badge, DataTable, EmptyState, Page, SegmentedControl, Tabs, Text, TopBar, type DataTableColumn } from "../ds";
import { fechaHora, telefono } from "../formato";
import { Enlace, navegar, useUbicacion } from "../router";
import type { FilaMensaje, ListaMensajes } from "../tipos";

export function Mensajes() {
  const { params } = useUbicacion();
  const canal = params.get("canal") ?? "";
  const estado = params.get("estado") ?? "";
  const pagina = Number(params.get("pagina") ?? "1") || 1;
  const { datos, error, cargando, recargar } = useDatos<ListaMensajes>(`/api/mensajes?canal=${canal}&estado=${estado}&pagina=${pagina}`);
  useAlCambiar(recargar);

  const ir = (c: string, e: string, p = 1) => {
    const q = new URLSearchParams();
    if (c) q.set("canal", c);
    if (e) q.set("estado", e);
    if (p > 1) q.set("pagina", String(p));
    navegar(q.size ? `/mensajes?${q}` : "/mensajes", { reemplazar: true });
  };
  const n = datos?.porEstado ?? {};
  const total = Object.values(n).reduce((a, b) => a + (b ?? 0), 0);

  const columnas: DataTableColumn<FilaMensaje>[] = [
    { key: "fecha", header: "Fecha", width: "132px", mono: true, render: (m) => fechaHora(m.enviadoEn ?? m.creadoEn) },
    {
      key: "inmobiliaria",
      header: "Inmobiliaria",
      width: "24%",
      render: (m) => (
        <Enlace href={`/leads/${m.inmobiliaria.id}`} style={{ fontWeight: 600, color: "var(--text-strong)" }}>
          {m.inmobiliaria.nombre}
        </Enlace>
      ),
    },
    { key: "canal", header: "Canal", width: "112px", render: (m) => <BadgeCanal canal={m.canal} /> },
    { key: "estado", header: "Estado", width: "120px", render: (m) => <BadgeMensaje estado={m.estado} /> },
    {
      key: "texto",
      header: "Mensaje",
      hideBelow: "md",
      render: (m) => (
        <span style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", minWidth: 0 }}>
          {m.plantilla ? (
            <Badge tone="neutral" size="sm" icon="file-text">
              {m.plantilla}
            </Badge>
          ) : null}
          <Text as="span" size="xs" truncate title={m.error ?? undefined} tone={m.error && m.estado !== "cancelado" ? "danger" : "body"}>
            {m.asunto ? `${m.asunto} · ` : ""}
            {m.extracto.replace(/\s+/g, " ")}
          </Text>
        </span>
      ),
    },
    { key: "destino", header: "Destino", width: "168px", mono: true, hideBelow: "md", render: (m) => (m.canal === "whatsapp" ? telefono(m.destinatario) : m.destinatario) },
  ];

  return (
    <>
      <TopBar
        title="Mensajes"
        subtitle="Todo lo que se preparó y se envió, por email y por WhatsApp."
        actions={
          <SegmentedControl
            label="Canal"
            size="sm"
            value={canal || "todos"}
            onChange={(v) => ir(v === "todos" ? "" : v, estado)}
            options={[
              { value: "todos", label: "Todos" },
              { value: "whatsapp", label: "WhatsApp", icon: "whatsapp" },
              { value: "email", label: "Email", icon: "mail" },
            ]}
          />
        }
      />
      <Page width="none">
        <Tabs
          value={estado}
          onChange={(v) => ir(canal, v)}
          tabs={[
            { value: "", label: "Todos", count: total },
            { value: "borrador", label: "Borradores", count: n.borrador ?? 0 },
            { value: "enviado", label: "Enviados", count: n.enviado ?? 0 },
            { value: "fallo", label: "Fallidos", count: n.fallo ?? 0, countTone: "warning" },
            { value: "cancelado", label: "Descartados", count: n.cancelado ?? 0 },
          ]}
        />
        {!datos ? (
          <Carga error={error} onReintentar={recargar} filas={8} />
        ) : (
          <DataTable
            columns={columnas}
            rows={datos.filas}
            rowKey={(m) => m.id}
            loading={cargando}
            rowHref={(m) => `/leads/${m.inmobiliaria.id}`}
            linkAs={Enlace}
            page={datos.pagina}
            pageSize={datos.porPagina}
            total={datos.total}
            onPageChange={(p) => ir(canal, estado, p)}
            minWidth="880px"
            empty={
              <EmptyState icon="inbox" title="No hay mensajes con este filtro" tone="plain">
                Los borradores de WhatsApp se preparan desde la cola.
              </EmptyState>
            }
          />
        )}
      </Page>
    </>
  );
}
